import { Language } from "@digipost/translate";
import type { ThunkAction } from "redux-thunk";

import { EntryPoint } from "../../../../../types/entrypoint";
import { Link } from "../../../../../types/link";
import { CompleteRegistrationPerson } from "../../../../../types/registration";
import { User } from "../../../../../types/session";
import { ParamState } from "../../../../../types/state";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../../../analytics/SiteImprove";
import { getJSON, post, remove } from "../../digipost-fetch";
import { getInitialState } from "../../entryPoint";
import { getLanguageFromPath } from "../../LanguageContext";
import { findURIWithWindowHostname, linkExist } from "../../linkUtils";
import { logError } from "../../serverlog";
import { fetchUser } from "../../session";
import {
  RegistrationStatus,
  RegistrationStatusAction,
  setRegistrationStatus,
} from "../org/org-actions";
import { ReduxState } from "../state";
import { ThunkContext } from "../thunkContext";

interface OAuthToken {
  appName: string;
  description: string;
  givenAccess: Array<string>;
  link: Array<Link>;
  missingAccess: Array<string>;
  redirectUri: string;
  scope: string;
  state: string;
  tokenId: string;
  tokenType: string;
  vendor: string;
}

type EntrypointFetchedAction = {
  type: "STORE_ENTRYPOINT";
  entrypoint: EntryPoint;
};

export function entrypointFetched(
  entrypoint: EntryPoint,
): EntrypointFetchedAction {
  return {
    type: "STORE_ENTRYPOINT",
    entrypoint: entrypoint,
  };
}

type UserFetchedAction = {
  type: "SESSION_FETCHED";
  user: User | undefined;
};

export function sessionFetched(user: User | undefined): UserFetchedAction {
  return {
    type: "SESSION_FETCHED",
    user: user,
  };
}

type ReopenLinkReceivedAction = {
  type: "REOPEN_LINK_RECEIVED";
  link: Array<Link>;
};

export function reopenLinkReceived(
  links: Array<Link>,
): ReopenLinkReceivedAction {
  return {
    type: "REOPEN_LINK_RECEIVED",
    link: links,
  };
}

export function toIDPortenAction(
  rootPath: string,
): ThunkAction<Promise<void>, ReduxState, never, never> {
  const language = getLanguageFromPath();
  return async (_dispatch, getStore) => {
    siteImproveEvent({
      category: SiteImproveCategories.PERSON_REGISTRATION,
      action: SiteImproveActions.CONTINUE,
      label: "/person/login",
    });

    const returnUrl = window.location.pathname;
    const targetUrl =
      `${returnUrl}/form`.replace("//", "/") + window.location.search;

    if (getStore().user.authIsRegistrering) {
      window.location.href = targetUrl;
      return;
    }

    const loginUrl = findURIWithWindowHostname("login_secure", getStore().link);
    const body = {
      authenticationRole: "REGISTRATION",
      authenticationType: "IDPORTEN_NIVAA_4",
      language: language,
      returnUrl: targetUrl,
      returnUrlError: `${rootPath}/person?loginfeil`,
      elevation: true,
      solvency: false,
      errorCallback: "dp.state.authError",
      successCallback: "dp.state.authSuccess",
    };

    const resp = await post(loginUrl, body, undefined);
    const url = resp.headers.get("location");
    if (url) {
      window.location.href = url;
    }
  };
}

function createBody(userinfo: Partial<User>): CompleteRegistrationPerson {
  const language = getLanguageFromPath();
  if (userinfo.krr) {
    return {
      language: language,
      email: userinfo.krr.epost.trim(),
      phone: userinfo.krr.nationalNumber.trim(),
      countryCode: userinfo.krr.countryCode.replace("+", "").trim(),
    };
  }

  return {
    language: language,
  };
}

export function completePersonRegistrationAction(
  rootPath: string,
  language: Language,
): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  RegistrationStatusAction
> {
  return async (dispatch, getStore, { navigate }) => {
    try {
      const { user, app, link } = getStore();
      const uri = findURIWithWindowHostname("register_person", link);
      const body = user.info ? createBody(user.info) : undefined;
      dispatch(setRegistrationStatus(RegistrationStatus.SUBMITTING));
      const response = await post(
        uri,
        body,
        app.csrfToken,
        `${rootPath}/person?logout=1`,
      );
      if (response.status === 409) {
        //Diverse conflicted state i Digipost
        const body409 = await response.json();

        if (body409["error-code"] === "REGISTRANT_ALREADY_REGISTRED") {
          siteImproveEvent({
            category: SiteImproveCategories.PERSON_REGISTRATION,
            action: SiteImproveActions.NEW_ACCOUNT,
            label: "/person/allerede_registrert",
          });

          window.location.href = `/postkasse/?language=${language}`;
        } else if (body409["error-code"] === "REGISTRANT_HAS_CLOSED_ACCOUNT") {
          siteImproveEvent({
            category: SiteImproveCategories.PERSON_REGISTRATION,
            action: SiteImproveActions.NEW_ACCOUNT,
            label: "/person/lukket_konto",
          });
          navigate(`${rootPath}/person/gjenapne`);
        } else if (
          body409["error-code"] === RegistrationStatus.REGISTRANT_TOO_YOUNG
        ) {
          dispatch(
            setRegistrationStatus(RegistrationStatus.REGISTRANT_TOO_YOUNG),
          );
        } else if (
          body409["error-code"] === RegistrationStatus.REGISTRANT_MISSES_NAME
        ) {
          dispatch(
            setRegistrationStatus(RegistrationStatus.REGISTRANT_MISSES_NAME),
          );
        } else {
          dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
        }
      } else {
        siteImproveEvent({
          category: SiteImproveCategories.PERSON_REGISTRATION,
          action: SiteImproveActions.NEW_ACCOUNT,
          label: "/person/registrering",
        });

        const urlParams = new URLSearchParams(window.location.search);
        const oAuthToken = urlParams.get("token");

        if (oAuthToken) {
          const getTokenUri = `/post/api/oauth/authorize/tokens/${oAuthToken}`;

          const tokenObject = await getJSON<OAuthToken>(getTokenUri);
          const approveUrl = findURIWithWindowHostname(
            "token_approve",
            tokenObject.link,
          );

          const entrypoint = (await getInitialState()) as EntryPoint;
          const tokenApproveResponse = await post(
            approveUrl,
            {},
            entrypoint.csrfToken,
          );
          const tokenApproveJson = await tokenApproveResponse.json();

          window.location.href = `${tokenObject.redirectUri}?&state=${tokenObject.state}&code=${tokenApproveJson.code}`;
        } else {
          window.location.href = `/postkasse/?language=${language}`;
        }
      }
    } catch (e) {
      siteImproveEvent({
        category: SiteImproveCategories.PERSON_REGISTRATION,
        action: SiteImproveActions.NEW_ACCOUNT,
        label: "/person/feilet",
      });
      dispatch(setRegistrationStatus(RegistrationStatus.FAILED));
      logError(e as Error);
    }
  };
}

export function fetchEntrypointAction(): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  UserFetchedAction | EntrypointFetchedAction
> {
  return async (dispatch): Promise<void> => {
    const entrypoint = (await getInitialState()) as EntryPoint;
    const sessionUrl = findURIWithWindowHostname(
      "session",
      entrypoint.rootLinks,
    );
    if (linkExist("register_logmein", entrypoint.rootLinks)) {
      await post(
        findURIWithWindowHostname("register_logmein", entrypoint.rootLinks),
        undefined,
        entrypoint.csrfToken,
      );
      window.location.href = "/postkasse/";
    } else {
      const user = await fetchUser(sessionUrl);
      dispatch(sessionFetched(user));
      dispatch(entrypointFetched(entrypoint));
      if (
        linkExist("reopen_account", entrypoint.rootLinks) &&
        window.location.href.indexOf("person/gjenapne") === -1
      ) {
        window.location.href = "/registrering/person/gjenapne";
      }
    }
  };
}

export function logoutAction(): ThunkAction<
  Promise<void>,
  ReduxState,
  never,
  never
> {
  return async (_dispatch, getStore) => {
    const logoutLink = findURIWithWindowHostname("logout", getStore().link);

    const finaldestination = `/registrering/${getStore().app.mode}`;

    const resp = await remove(logoutLink, undefined, {
      FINAL_DESTINATION: finaldestination,
    });

    const link = resp.headers.get("location");
    window.sessionStorage.clear();
    if (link) {
      window.location.href = link;
    } else {
      window.location.href = finaldestination;
    }
  };
}

type SetControlFlowAction = {
  type: "SET_CONTROL_FLOW";
  payload: ParamState;
};

function setControlFlowAction(controlFlow: ParamState): SetControlFlowAction {
  return {
    type: "SET_CONTROL_FLOW",
    payload: controlFlow,
  };
}

function getAndSaveParam(name: string): string | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  const searchParam = searchParams.get(name);

  if (searchParam) {
    window.sessionStorage.setItem(name, searchParam);
    return searchParam;
  }

  return window.sessionStorage.getItem(name) ?? undefined;
}

export function setControlFlow(): ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkContext,
  SetControlFlowAction
> {
  return async (dispatch) => {
    const partnerId = getAndSaveParam("partner-id");
    const redirectUrl = getAndSaveParam("redirect-url");
    const redirectUrlSignering = getAndSaveParam("redirect-url-signering");
    const nonce = getAndSaveParam("nonce");
    const preselectedOrg = getAndSaveParam("org");
    const integrationType = getAndSaveParam("integration-type") ?? "1";

    if (partnerId) {
      dispatch(
        setControlFlowAction({
          mode: "behandler",
          partnerId,
          redirectUrl,
          nonce,
          preselectedOrg,
          integrationType,
        }),
      );
      return;
    }

    if (redirectUrlSignering) {
      dispatch(
        setControlFlowAction({
          mode: "signering",
          redirectUrl: redirectUrlSignering,
        }),
      );
      return;
    }

    dispatch(
      setControlFlowAction({
        mode: "bedrift",
      }),
    );
  };
}

export type AppAction =
  | EntrypointFetchedAction
  | UserFetchedAction
  | ReopenLinkReceivedAction
  | SetControlFlowAction;
